import { Box } from '@material-ui/core';
import { patchSetPricingIdsToAgent, postSyncPricingsToCdnByAgentId, Pricing } from '@nemuru-eng/js-api-client';
import { getPricingsFromCdnByAgentId } from '@nemuru-eng/js-api-client/slim';

import { PricingData } from '@/components/modules/AgentDetail/AgentPricings/types/PricingData';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

interface SetPricingsDialogProps {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  agentId: string;
  selectedPricings: PricingData[];
  getAgentPricingsAndCloseDialog: () => Promise<void>;
  setLoading: (boolean) => void;
  agentPricings?: Pricing[];
}

export function SetPricingsDialog({
  opened,
  setOpened,
  agentId,
  selectedPricings,
  getAgentPricingsAndCloseDialog,
  setLoading,
  agentPricings,
}: SetPricingsDialogProps) {
  const { request, loading } = useRequest();
  const { revalidate } = useCache(getPricingsFromCdnByAgentId, { agentId });

  const unchangedPricings = agentPricings
    ? selectedPricings.filter((pricing) => agentPricings.some((agentPricing) => agentPricing.id === pricing.id))
    : [];

  const pricingsToAdd = agentPricings
    ? selectedPricings.filter((pricing) => !agentPricings.some((agentPricing) => agentPricing.id === pricing.id))
    : selectedPricings;

  const pricingsToRemove = agentPricings
    ? agentPricings.filter((agentPricing) => !selectedPricings.some((pricing) => pricing.id === agentPricing.id))
    : [];

  const onSubmit = async (): Promise<void> => {
    await request(patchSetPricingIdsToAgent, {
      id: agentId,
      pricingIds: selectedPricings.map((pricing) => pricing.id),
    });
    await getAgentPricingsAndCloseDialog();
    await request(postSyncPricingsToCdnByAgentId, { agentId });
    setOpened(false);
    setLoading(false);
    revalidate();
  };

  return (
    <ActionDialog
      Title={<Text tId="SET_PRICINGS_DIALOG" />}
      Description={<Text tId={'SET_PRICINGS_DIALOG_TEXT'} />}
      MainButton={<Text tId={'SET_PRICINGS_DIALOG_BTN'} />}
      opened={opened}
      setOpened={setOpened}
      onSubmit={onSubmit}
      handleClose={() => setOpened(false)}
      loading={loading}
    >
      <Box mt={3} display={'flex'} flexDirection={'column'} gridGap={16}>
        <Box>
          <Font variant={'body2'} color={'textPrimary'}>
            <Text tId={'UNCHANGED_PRICINGS'} />
            {` (${unchangedPricings.length})`}
          </Font>
          <ul>
            {unchangedPricings.map((pricing) => (
              <li key={pricing.id}>
                <Font variant={'body2'} color={'textPrimary'}>
                  {pricing.name}
                </Font>
              </li>
            ))}
          </ul>
        </Box>
        <Box>
          <Font variant={'body2'} color={'textPrimary'}>
            <Text tId={'PRICINGS_TO_ADD'} />
            {` (${pricingsToAdd.length})`}
          </Font>
          <ul>
            {pricingsToAdd.map((pricing) => (
              <li key={pricing.id}>
                <Font variant={'body2'} color={'textPrimary'}>
                  {pricing.name}
                </Font>
              </li>
            ))}
          </ul>
        </Box>
        <Box>
          <Font variant={'body2'} color={'textPrimary'}>
            <Text tId={'PRICINGS_TO_REMOVE'} />
            {` (${pricingsToRemove.length})`}
          </Font>
          <ul>
            {pricingsToRemove.map((pricing) => (
              <li key={pricing.id}>
                <Font variant={'body2'} color={'textPrimary'}>
                  {pricing.name}
                </Font>
              </li>
            ))}
          </ul>
        </Box>
      </Box>
    </ActionDialog>
  );
}
